import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Bell, BellOff, Loader2 } from 'lucide-react';
import { API } from '@/config';

// Convert URL-safe base64 string to Uint8Array
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

export const PushNotificationToggle = ({ userId, role }) => {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    const ok = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setSupported(ok);
    if (ok) {
      setPermission(Notification.permission);
      // Check if already subscribed
      navigator.serviceWorker.ready.then(reg => reg.pushManager.getSubscription()).then(sub => {
        setSubscribed(!!sub);
      }).catch(() => {});
    }
  }, []);

  const subscribe = async () => {
    setLoading(true);
    setFeedback('');
    try {
      // Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        setFeedback('Απαιτείται άδεια ειδοποιήσεων από τον browser.');
        return;
      }

      // Get VAPID public key
      const { data } = await axios.get(`${API}/push/vapid-public-key`);
      if (!data.publicKey) {
        setFeedback('Σφάλμα: δεν είναι διαθέσιμο το κλειδί διακομιστή.');
        return;
      }

      // Subscribe via service worker
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });

      // Send subscription to backend
      await axios.post(`${API}/push/subscribe`, {
        user_id: userId,
        role,
        subscription: sub.toJSON(),
      });

      setSubscribed(true);
      setFeedback('Ειδοποιήσεις ενεργοποιήθηκαν!');
      setTimeout(() => setFeedback(''), 3000);
    } catch (err) {
      console.error('Push subscribe failed', err);
      setFeedback('Αποτυχία εγγραφής. Δοκιμάστε ξανά.');
    } finally {
      setLoading(false);
    }
  };

  const unsubscribe = async () => {
    setLoading(true);
    setFeedback('');
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await axios.post(`${API}/push/unsubscribe`, { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setSubscribed(false);
      setFeedback('Ειδοποιήσεις απενεργοποιήθηκαν.');
      setTimeout(() => setFeedback(''), 3000);
    } catch (err) {
      console.error('Push unsubscribe failed', err);
      setFeedback('Αποτυχία απεγγραφής.');
    } finally {
      setLoading(false);
    }
  };

  if (!supported) {
    return (
      <div className="text-xs text-slate-500 px-3 py-2" data-testid="push-not-supported">
        Ο browser δεν υποστηρίζει push notifications
      </div>
    );
  }

  return (
    <div className="inline-flex flex-col items-start gap-1" data-testid="push-toggle-container">
      <button
        onClick={subscribed ? unsubscribe : subscribe}
        disabled={loading || permission === 'denied'}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
          subscribed
            ? 'bg-green-500/15 text-green-400 border-green-500/30 hover:bg-green-500/25'
            : 'bg-gold/15 text-gold border-gold/40 hover:bg-gold/25'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
        data-testid="push-toggle-btn"
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : subscribed ? (
          <Bell className="w-4 h-4" />
        ) : (
          <BellOff className="w-4 h-4" />
        )}
        <span>
          {subscribed
            ? 'Ειδοποιήσεις: Ενεργές'
            : permission === 'denied'
            ? 'Αποκλεισμένες από browser'
            : 'Ενεργοποίηση Push Notifications'}
        </span>
      </button>
      {feedback && (
        <p className="text-xs text-slate-400 px-1" data-testid="push-feedback">
          {feedback}
        </p>
      )}
      {permission === 'denied' && !subscribed && (
        <p className="text-xs text-slate-500 px-1">
          Αλλάξτε τη ρύθμιση από τις ρυθμίσεις του browser
        </p>
      )}
    </div>
  );
};

export default PushNotificationToggle;
